import { motion, useReducedMotion } from 'framer-motion';
import { Box, Flex, IconButton } from '@chakra-ui/react';

/**
 * The inbox's motion vocabulary — one place, so every surface moves the same way.
 *
 * framer-motion here is 4.1 (pinned by Chakra v1's peer range); `motion(Box)`
 * is the supported wrapper in that version. ⚠ Wrapping a Chakra component hands
 * the `transition` prop to framer, so pass framer's object form, never Chakra's
 * CSS string.
 *
 * ★ ANIMATE SINGLETONS, NOT LISTS. The thread can hold hundreds of bubbles;
 * `layout` props and `AnimatePresence` around list items are the two things
 * that make a long chat stutter. Bubbles animate only on their first mount, and
 * everything else that moves is a single element: the reply strip, the
 * jump-to-latest button, the mic/send morph, the pane swap.
 */
export const MotionBox = motion(Box);
export const MotionFlex = motion(Flex);
export const MotionIconButton = motion(IconButton);

/** WhatsApp's ease: quick out, soft landing. */
export const EASE = [0.2, 0, 0, 1];

/**
 * Durations that collapse to zero when the OS asks for reduced motion. The
 * global stylesheet already kills CSS transitions in that case; framer needs
 * to be told explicitly.
 */
export function useMotionPrefs() {
	const reduced = useReducedMotion();
	return {
		reduced,
		/** seconds for framer, from the ms the design speaks in */
		dur: (ms) => (reduced ? 0 : ms / 1000),
	};
}

export const fadeUp = {
	initial: { opacity: 0, y: 12, scale: 0.96 },
	animate: { opacity: 1, y: 0, scale: 1 },
};

export const scaleIn = {
	initial: { opacity: 0, scale: 0.6 },
	animate: { opacity: 1, scale: 1 },
	exit: { opacity: 0, scale: 0.6 },
};

export const slideUp = {
	initial: { opacity: 0, height: 0 },
	animate: { opacity: 1, height: 'auto' },
	exit: { opacity: 0, height: 0 },
};
